/**
 * `/affiliate-payouts` - the operator affiliate PAYOUT queue (OCP-493).
 *
 * Lists affiliates with an `eligible` balance owed (totals + count by
 * currency), lets the operator REVEAL the (audited) decrypted payout
 * details to make the transfer, then enter a payout reference and mark
 * that affiliate + currency PAID. Marking paid is atomic + idempotent
 * server-side: only `eligible` commissions flip to `paid`.
 *
 * Pairs with:
 *   GET  /v1/admin/affiliate-payouts
 *   GET  /v1/admin/accounts/:id/affiliate/payout-details          (masked, OCP-930)
 *   POST /v1/admin/accounts/:id/affiliate/payout-details/reveal   (plaintext: a super_admin person + second factor + reason, audited)
 *   POST /v1/admin/accounts/:id/affiliate/payouts          (mark paid)
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { useTransientState } from '../lib/useTransientState';

interface QueueCurrency {
  currency: string;
  eligible_cents: number;
  count: number;
}

interface QueueEntry {
  affiliate_customer_id: string;
  email: string;
  display_name: string | null;
  payout_method: 'sepa' | 'paypal' | 'other' | null;
  payout_details_set: boolean;
  has_profile: boolean;
  currencies: QueueCurrency[];
}

interface RevealedDetails {
  payout_method: 'sepa' | 'paypal' | 'other' | null;
  payout_details: string | null;
  payout_details_set: boolean;
}

/** Cents -> localized currency string; falls back to `<amount> <CODE>`. */
export function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

export function AdminAffiliatePayoutsPage() {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useTransientState<string | null>(null, 4000);

  const load = useCallback(async () => {
    try {
      const r = await api<{ payouts: QueueEntry[] }>('/v1/admin/affiliate-payouts');
      setEntries(r.payouts);
      setState('ready');
    } catch (e) {
      setError(e instanceof ApiError ? `Load failed (HTTP ${e.status}).` : 'Load failed.');
      setState('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function onPaid(summary: string) {
    setFlash(summary);
    await load();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Affiliate payouts</h1>
        <p className="mt-1 text-sm text-gray-500">
          Affiliates with matured (eligible) commissions owed. Reveal the payout details, make the
          transfer, then enter your reference and mark it paid. Only eligible commissions are paid -
          held (still inside the refund window) balances are not shown here.
        </p>
      </div>

      {flash && (
        <div className="rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-200">
          {flash}
        </div>
      )}
      {error && (
        <div className="rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {state === 'loading' && <div className="text-sm text-gray-500">Loading…</div>}

      {state === 'ready' && entries.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-4 py-8 text-center text-sm text-gray-500">
          Nothing to pay out. No affiliate has an eligible balance right now.
        </div>
      )}

      {state === 'ready' &&
        entries.map((e) => <AffiliatePayoutRow key={e.affiliate_customer_id} entry={e} onPaid={onPaid} />)}
    </div>
  );
}

function AffiliatePayoutRow({
  entry,
  onPaid,
}: {
  entry: QueueEntry;
  onPaid: (summary: string) => Promise<void>;
}) {
  const [revealed, setRevealed] = useState<RevealedDetails | null>(null);
  const [masked, setMasked] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);

  // OCP-930 - the masked destination is the default view (no plaintext, no audit row).
  useEffect(() => {
    if (!entry.payout_details_set) return;
    let alive = true;
    void api<{ payout_details: { payout_details_masked: string | null } }>(`/v1/admin/accounts/${entry.affiliate_customer_id}/affiliate/payout-details`)
      .then((r) => {
        if (alive) setMasked(r.payout_details.payout_details_masked);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [entry.affiliate_customer_id, entry.payout_details_set]);

  async function reveal() {
    if (reason.trim().length < 10) {
      setRevealError('Name why you need the full details (at least 10 characters) - it is audited.');
      return;
    }
    setRevealing(true);
    setRevealError(null);
    try {
      const r = await api<{ payout_details: RevealedDetails }>(
        `/v1/admin/accounts/${entry.affiliate_customer_id}/affiliate/payout-details/reveal`,
        { method: 'POST', body: { reason: reason.trim() } },
      );
      setRevealed(r.payout_details);
      setAsking(false);
    } catch (err) {
      const code = err instanceof ApiError ? (err.body as { error?: string } | null)?.error : undefined;
      setRevealError(
        code === 'forbidden_for_role' || code === 'needs_a_person'
          ? 'Only a super_admin person may reveal payout details.'
          : code === 'second_factor_required' || code === 'mfa_enrollment_required'
            ? 'Sign in with your second factor to reveal payout details.'
            : err instanceof ApiError ? `Reveal failed (HTTP ${err.status}).` : 'Reveal failed.',
      );
    } finally {
      setRevealing(false);
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {entry.display_name || entry.email}
          </div>
          {entry.display_name && (
            <div className="truncate text-xs text-gray-500">{entry.email}</div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="rounded border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-gray-600 dark:text-gray-300">
            {entry.payout_method ? entry.payout_method.toUpperCase() : 'no method'}
          </span>
          {!entry.has_profile && (
            <span className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-amber-800 dark:text-amber-200">
              profile revoked
            </span>
          )}
        </div>
      </div>

      {/* Payout details reveal - an explicit, audited action. */}
      <div className="mt-3">
        {revealed ? (
          <div className="rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">Payout details</div>
            {revealed.payout_details ? (
              <code className="mt-1 block break-all font-mono text-sm text-gray-900 dark:text-gray-100">
                {revealed.payout_details}
              </code>
            ) : (
              <div className="mt-1 text-sm text-gray-500">No payout details on file for this affiliate.</div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {masked && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                Payout details: <code data-testid="payout-masked" className="font-mono">{masked}</code>
              </div>
            )}
            {asking ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="Why (audited), e.g. SEPA transfer for the October payout"
                  aria-label="Reveal reason"
                  className="min-w-[16rem] flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-xs"
                />
                <button
                  type="button"
                  disabled={revealing}
                  onClick={() => void reveal()}
                  className="rounded bg-brand-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {revealing ? 'Revealing…' : 'Reveal'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAsking(true)}
                className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
              >
                Reveal full payout details (super_admin, audited)
              </button>
            )}
          </div>
        )}
        {revealError && <div className="mt-1 text-xs text-red-700 dark:text-red-300">{revealError}</div>}
      </div>

      <div className="mt-3 space-y-2">
        {entry.currencies.map((c) => (
          <CurrencyPayoutForm
            key={c.currency}
            affiliateCustomerId={entry.affiliate_customer_id}
            email={entry.email}
            currency={c}
            onPaid={onPaid}
          />
        ))}
      </div>
    </div>
  );
}

function CurrencyPayoutForm({
  affiliateCustomerId,
  email,
  currency,
  onPaid,
}: {
  affiliateCustomerId: string;
  email: string;
  currency: QueueCurrency;
  onPaid: (summary: string) => Promise<void>;
}) {
  const [payoutRef, setPayoutRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function markPaid() {
    if (submitting) return;
    if (payoutRef.trim() === '') {
      setError('Enter a payout reference (the transfer id) before marking paid.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ count: number; total_cents: number; currency: string; payout_ref: string }>(
        `/v1/admin/accounts/${affiliateCustomerId}/affiliate/payouts`,
        { method: 'POST', body: { currency: currency.currency, payout_ref: payoutRef.trim() } },
      );
      if (r.count === 0) {
        setError('Nothing was paid - no eligible commissions in this currency (already paid?).');
        return;
      }
      await onPaid(
        `Paid ${email}: ${formatMoney(r.total_cents, r.currency)} (${r.count} commission${
          r.count === 1 ? '' : 's'
        }, ref ${r.payout_ref}).`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? `Payout failed (HTTP ${err.status}).` : 'Payout failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 px-3 py-2">
      <div className="min-w-[9rem]">
        <div className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
          {formatMoney(currency.eligible_cents, currency.currency)}
        </div>
        <div className="text-[11px] text-gray-500">
          {currency.count} eligible commission{currency.count === 1 ? '' : 's'}
        </div>
      </div>
      <input
        type="text"
        value={payoutRef}
        onChange={(ev) => setPayoutRef(ev.target.value)}
        placeholder="Payout reference (SEPA / PayPal id)"
        className="min-w-0 flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm"
      />
      <button
        type="button"
        disabled={submitting}
        onClick={() => void markPaid()}
        className="shrink-0 rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
      >
        {submitting ? 'Marking…' : 'Mark paid'}
      </button>
      {error && <div className="w-full text-xs text-red-700 dark:text-red-300">{error}</div>}
    </div>
  );
}
